import React, { useEffect } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { toast } from 'react-toastify';
import { SwitchOnOff } from '../../../../components/commons/SwitchOnOff/switchOnOff';
import { BrightnessSelectorBtn } from '../../../../components/commons/BrightnessSelector/brightnessSelector';
import { ColorSelector } from '../../../../components/commons/ColorSelector/ColorSelector';
import Strings from '../../../../i18n';
import { ILightsZonesListProps } from '../lightsZonesProps';
import { ColumnCommandsStyle } from './lightsZonesListStyles';
import { ILightZone } from '../../../../types/Lights/lightZoneInterfaces';
import {
  useSendBrightnessCommandMutation,
  useSendColorCommandMutation,
  useSendOnOffCommandMutation
} from '../../../../services/commandsService/commandsService';
import {
  ICommandBrightness,
  ICommandOnOff,
  ICommandSetColor
} from '../../../../types/commandsInterface';
import { AllLightsAndZonesComponent } from '../allLightsAndZones/allLightsAndZonesComponent';
import { ContainerTable } from '../../../locations/locationsList/locationListStyles';
import { StatusLightsZones } from './statusLightsZones/statusLightsZones';

export const LightsZonesList = ({
  data,
  searchValue,
  selectedLightsZones,
  reloadTable,
  setSelectedLightsZones
}: ILightsZonesListProps) => {
  const [setColor, { isSuccess: isSuccessSetColor }] =
    useSendColorCommandMutation();

  const [setBrightness, { isSuccess: isSuccessSetBrightness }] =
    useSendBrightnessCommandMutation();

  const [setSwitch, { isSuccess: isSuccessSetSwitch }] =
    useSendOnOffCommandMutation();

  const filteredData = data?.filter((item: ILightZone) => {
    const searchValueLowerCase = searchValue.toLowerCase();
    let includesSearchValue = false;
    includesSearchValue = item.name
      .toLowerCase()
      .includes(searchValueLowerCase);

    return includesSearchValue && !item.isHidden;
  });

  const sendBrightnessCommand = (payload: ICommandBrightness) => {
    setBrightness(payload);
  };

  const sendColorCommand = (payload: ICommandSetColor) => {
    setColor(payload);
  };

  const sendSwitchCommand = (payload: ICommandOnOff) => {
    setSwitch(payload);
  };

  useEffect(() => {
    if (isSuccessSetColor) {
      toast.success(Strings.trimLights.setColorSuccess);
    }
  }, [isSuccessSetColor]);

  useEffect(() => {
    if (isSuccessSetBrightness) {
      toast.success(Strings.commands.setBrightnessSuccess);
    }
  }, [isSuccessSetBrightness]);

  useEffect(() => {
    if (isSuccessSetSwitch) {
      toast.success(Strings.commands.commandSentSuccessfully);
    }
  }, [isSuccessSetSwitch]);

  const getCommands = (item: ILightZone) => {
    return (
      <div className="d-flex align-items-center my-1">
        <SwitchOnOff
          isOnLight={item.isOn}
          onSwitch={(value) =>
            sendSwitchCommand({
              id: item.id,
              method: value ? 'On' : 'Off',
              type: item.isZone ? 'Zone' : 'Light'
            })
          }
        />
        <BrightnessSelectorBtn
          lightBrightnessId={item.lightBrightnessId}
          onChange={(brightnessId) =>
            sendBrightnessCommand({
              brightness: brightnessId,
              id: item.id,
              type: item.isZone ? 'Zone' : 'Light'
            })
          }
        />
        <ColorSelector
          status={{
            colorId: item.colorId,
            patternSpeed: item.patternSpeed,
            sparkleDuration: item.sparkleDuration
          }}
          capabilities={item.capabilities}
          onColorChange={(colorId) => {
            sendColorCommand({
              id: item.id,
              colorId,
              type: item.isZone ? 'Zone' : 'Light'
            });
          }}
        />
        {item.name !== Strings.lightsZones.allZones && (
          <StatusLightsZones lightZone={item} />
        )}
      </div>
    );
  };

  const getMacAndType = (item: ILightZone) => {
    const last4Chart = item.macAddress.slice(-4);
    return (
      <span>
        {item.controllerTypeName}: {last4Chart}
      </span>
    );
  };

  return (
    <ContainerTable component="main">
      <DataTable
        scrollHeight="flex"
        selection={selectedLightsZones}
        selectionMode="multiple"
        metaKeySelection
        onSelectionChange={(e) => {
          setSelectedLightsZones(e.value);
        }}
        rowHover
        header={
          <AllLightsAndZonesComponent onSuccessCommand={() => reloadTable()} />
        }
        value={filteredData}
        size="small"
        scrollable
        paginator={filteredData && filteredData.length >= 50}
        selectionPageOnly
        rows={50}
        rowsPerPageOptions={[
          5,
          10,
          25,
          filteredData ? filteredData.length : 50
        ]}
        tableStyle={{ padding: 0, margin: 0, fontSize: '14px' }}
      >
        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
        <Column field="name" header="Name" sortable style={{ flex: 1 }} />

        <Column
          header="Controller"
          sortable
          body={getMacAndType}
          style={{ flex: 1 }}
        />
        <Column field="typeName" header="Type" sortable style={{ flex: 1 }} />
        <Column
          field="zoneNumber"
          header="Zone number"
          sortable
          style={{ width: '150px', textAlign: 'center' }}
        />
        <Column
          field=""
          header="Commands"
          sortable
          body={getCommands}
          style={ColumnCommandsStyle}
        />
      </DataTable>
    </ContainerTable>
  );
};
